import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmModal';
import { submissionsAPI, documentsAPI } from '../utils/api';
import { format } from 'date-fns';
import {
  canEdit,
  canDelete,
  canSubmitToTwilio,
  canRecreateVerification,
  canAddNotes,
  canChangeStatus,
  canUploadDocuments,
  canDeleteDocuments
} from '../utils/permissions';
import {
  BUSINESS_REGISTRATION_AUTHORITIES,
  BUSINESS_TYPES,
  getBusinessTypeLabel,
  getBusinessRegistrationAuthorityLabel
} from '../constants/businessFields';
import TwilioErrorModal from '../components/TwilioErrorModal';
import TwilioRejectionReason from '../components/TwilioRejectionReason';

export default function SubmissionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [auditLogs, setAuditLogs] = useState([]);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [twilioErrorResponse, setTwilioErrorResponse] = useState(null);
  const [recreating, setRecreating] = useState(false);

  useEffect(() => {
    loadSubmission();
    loadDocuments();
  }, [id]);

  const loadSubmission = async () => {
    try {
      const [submissionRes, auditRes] = await Promise.all([
        submissionsAPI.getById(id),
        submissionsAPI.getAuditLog(id, 50)
      ]);
      setSubmission(submissionRes.data.submission);
      setAuditLogs(auditRes.data.audit_logs || []);
      setEditedData(submissionRes.data.submission);
    } catch (error) {
      console.error('Failed to load submission:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel edit - reset data
      setEditedData(submission);
    }
    setIsEditing(!isEditing);
  };

  const handleFieldChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Format phone number to (xxx) xxx-xxxx format
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return 'N/A';
    // Remove all non-numeric characters
    const cleaned = phoneNumber.replace(/\D/g, '');
    // Format as (xxx) xxx-xxxx
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    if (cleaned.length === 11 && cleaned[0] === '1') {
      return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    // Return as-is if it doesn't match expected format
    return phoneNumber;
  };

  const handleBusinessDetailChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      business_details: {
        ...prev.business_details,
        [field]: value
      }
    }));
  };

  const handleAddressChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      business_details: {
        ...prev.business_details,
        address: {
          ...prev.business_details.address,
          [field]: value
        }
      }
    }));
  };

  const handleSaveChanges = async () => {
    try {
      const updateData = {
        organization_name: editedData.organization_name,
        contact_name: editedData.contact_name,
        contact_email: editedData.contact_email,
        contact_phone: editedData.contact_phone,
        zendesk_org_id: editedData.zendesk_org_id,
        zendesk_ticket_id: editedData.zendesk_ticket_id,
        phone_number: editedData.phone_number,
        phone_number_type: editedData.phone_number_type,
        current_provider: editedData.current_provider,
        business_details: editedData.business_details,
        submission_type: editedData.submission_type
      };

      // Only include suppress_emails if explicitly set (column may not exist on older DBs)
      if (editedData.suppress_emails !== undefined) {
        updateData.suppress_emails = editedData.suppress_emails;
      }

      const response = await submissionsAPI.update(id, updateData);

      toast.success(`Saved! ${response.data.changes?.length || 0} field(s) updated.`);
      setIsEditing(false);
      await loadSubmission(); // Reload to get updated audit log
    } catch (error) {
      toast.error(`Failed to save changes: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;

    try {
      await submissionsAPI.addNote(id, noteText);
      setNoteText('');
      loadSubmission();
      toast.success('Note added');
    } catch (error) {
      toast.error(`Failed to add note: ${error.response?.data?.error || error.message}`);
    }
  };

  const loadDocuments = async () => {
    try {
      const response = await documentsAPI.getBySubmissionId(id);
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Failed to load documents:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      await documentsAPI.upload(id, files);
      toast.success(`${files.length} file(s) uploaded successfully`);
      loadDocuments();
      e.target.value = ''; // Reset file input
    } catch (error) {
      toast.error(`Failed to upload files: ${error.response?.data?.error || error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId, fileName) => {
    const confirmed = await confirm({
      title: 'Delete Document',
      message: `Are you sure you want to delete "${fileName}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'delete'
    });
    if (!confirmed) return;

    try {
      await documentsAPI.delete(documentId);
      toast.success('Document deleted successfully');
      loadDocuments();
    } catch (error) {
      toast.error(`Failed to delete document: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await submissionsAPI.updateStatus(id, newStatus);
      loadSubmission();
      toast.success(`Status updated to "${formatStatusLabel(newStatus)}"`);
    } catch (error) {
      toast.error(`Failed to update status: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleSubmitToTwilio = async () => {
    const confirmed = await confirm({
      title: 'Submit to Twilio',
      message: 'Are you sure you want to submit this verification request to Twilio? This will start the review process.',
      confirmText: 'Submit',
      cancelText: 'Cancel',
      variant: 'primary'
    });
    if (!confirmed) return;

    try {
      await submissionsAPI.submitToTwilio(id);
      toast.success('Successfully submitted to Twilio!');
      loadSubmission();
    } catch (error) {
      // If the API returned a structured error, show the detailed, field-aware
      // modal with guidance. Otherwise fall back to a toast (network errors etc).
      const errorResponse = error.response?.data;
      if (errorResponse && (errorResponse.field || errorResponse.code || errorResponse.guidance || errorResponse.error)) {
        setTwilioErrorResponse(errorResponse);
      } else {
        toast.error(`Failed to submit to Twilio: ${error.message || 'An unknown error occurred'}`);
      }
    }
  };

  /**
   * Recovery path for a rejected verification whose 7-day Twilio edit window has
   * closed. Without this the submission is frozen in the app: it can't be
   * corrected in place and Twilio won't accept a second verification for the
   * same number, so the only way out used to be the Twilio console.
   *
   * Irreversible, so it is admin-only and double-confirmed. The backend
   * independently refuses to delete anything still editable, approved, or in
   * review, and answers 409 RECREATE_NOT_ALLOWED in that case.
   */
  const handleRecreateVerification = async () => {
    const confirmed = await confirm({
      title: 'Recreate Twilio Verification',
      message: (
        <div className="text-sm text-gray-500 space-y-2">
          <p>
            This permanently deletes the rejected verification at Twilio and submits a
            brand new one.
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Deleting cannot be undone.</li>
            <li>
              The new request goes to the back of Twilio&rsquo;s standard review queue,
              not the priority queue.
            </li>
            <li>
              Only do this once the rejection reasons have actually been corrected on
              this submission.
            </li>
          </ul>
          <p>
            If the 7-day edit window is still open, cancel and use &ldquo;Submit to
            Twilio&rdquo; instead &mdash; that keeps priority review.
          </p>
        </div>
      ),
      confirmText: 'Delete and Resubmit',
      cancelText: 'Cancel',
      variant: 'danger'
    });
    if (!confirmed) return;

    setRecreating(true);
    try {
      const response = await submissionsAPI.recreateVerification(id);
      toast.success(response.data?.message || 'Verification recreated and submitted to Twilio.');
      setTwilioErrorResponse(null);
      loadSubmission();
    } catch (error) {
      const errorResponse = error.response?.data;
      if (errorResponse && (errorResponse.field || errorResponse.code || errorResponse.guidance || errorResponse.error)) {
        setTwilioErrorResponse(errorResponse);
      } else {
        toast.error(`Failed to recreate verification: ${error.message || 'An unknown error occurred'}`);
      }
    } finally {
      setRecreating(false);
    }
  };

  const handleCheckTwilioStatus = async () => {
    setCheckingStatus(true);
    try {
      const response = await submissionsAPI.checkTwilioStatus(id);
      const { status_changed, new_status, old_status, rejection_reason, current_status } = response.data;

      if (status_changed) {
        toast.success(`Status updated from "${formatStatusLabel(old_status)}" to "${formatStatusLabel(new_status)}". Zendesk ticket has been updated and customer notified.`);
      } else {
        // Status hasn't changed - show appropriate message based on current status
        if (rejection_reason) {
          toast.success('Rejection details refreshed from Twilio.');
        } else if (current_status === 'TWILIO_REJECTED') {
          toast.info('Status unchanged. Submission is rejected by Twilio.');
        } else if (current_status === 'TWILIO_APPROVED') {
          toast.info('Status unchanged. Submission is approved by Twilio.');
        } else {
          toast.info('Status unchanged. Campaign is still pending Twilio review.');
        }
      }

      loadSubmission();
    } catch (error) {
      toast.error(`Failed to check Twilio status: ${error.response?.data?.error || error.message}`);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleCheckProfileStatus = async () => {
    setCheckingStatus(true);
    try {
      const response = await submissionsAPI.checkProfileStatus(id);
      const { profile_approved, verification_submitted, message, profile_status } = response.data;

      if (profile_approved && verification_submitted) {
        toast.success(message);
      } else {
        toast.info(`Profile status: ${profile_status}. ${message}`);
      }

      loadSubmission();
    } catch (error) {
      toast.error(`Failed to check profile status: ${error.response?.data?.error || error.message}`);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleApprove = async () => {
    const confirmed = await confirm({
      title: 'Approve Submission',
      message: 'Mark this submission as approved and notify the customer via email?',
      confirmText: 'Approve',
      cancelText: 'Cancel',
      variant: 'success'
    });
    if (!confirmed) return;

    try {
      await submissionsAPI.approve(id);
      toast.success('Submission approved and customer notified!');
      loadSubmission();
    } catch (error) {
      toast.error(`Failed to approve: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.warning('Please provide a rejection reason');
      return;
    }

    try {
      await submissionsAPI.reject(id, rejectReason);
      toast.success('Submission rejected and customer notified!');
      setShowRejectModal(false);
      setRejectReason('');
      loadSubmission();
    } catch (error) {
      toast.error(`Failed to reject: ${error.response?.data?.error || error.message}`);
    }
  };

  // Helper function to display Twilio status with friendly names
  // Safely pretty-print the stored Twilio response without letting a malformed
  // JSON string throw during render.
  const formatTwilioResponse = (resp) => {
    try {
      const value = typeof resp === 'string' ? JSON.parse(resp) : resp;
      return JSON.stringify(value, null, 2);
    } catch {
      return typeof resp === 'string' ? resp : String(resp);
    }
  };

  const getTwilioStatusLabel = (twilioStatus) => {
    if (!twilioStatus) return 'Not Checked';

    const statusLabels = {
      'PENDING_REVIEW': 'Pending Review',
      'IN_REVIEW': 'In Review',
      'TWILIO_APPROVED': 'Approved',
      'TWILIO_REJECTED': 'Rejected',
      'PROVISIONALLY_APPROVED': 'Provisionally Approved'
    };

    // If status is in the map, use it; otherwise format with proper capitalization
    if (statusLabels[twilioStatus]) {
      return statusLabels[twilioStatus];
    }

    // Fallback: Convert underscores to spaces and capitalize each word
    return twilioStatus
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  };

  // Helper function to format internal status labels (for toasts, etc.)
  const formatStatusLabel = (status) => {
    if (!status) return '';

    // Convert underscores to spaces and capitalize each word
    return status
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading submission...</p>
        </div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Submission Not Found</h2>
          <Link to="/dashboard" className="text-primary-600 hover:text-primary-700">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div className="w-full lg:w-auto">
              <Link to="/dashboard" className="text-primary-600 hover:text-primary-700 text-sm mb-2 inline-block">
                ← Back to Dashboard
              </Link>
              <div className="flex items-center gap-3">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">{submission.organization_name}</h1>
                {isEditing ? (
                  <select
                    value={editedData.submission_type || 'text_giving'}
                    onChange={(e) => handleFieldChange('submission_type', e.target.value)}
                    className="text-sm px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="text_giving">Text Giving</option>
                    <option value="messaging">Messaging</option>
                  </select>
                ) : (
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    submission.submission_type === 'messaging'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {submission.submission_type === 'messaging' ? 'Messaging' : 'Text Giving'}
                  </span>
                )}
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mt-1">
                <p className="text-xs sm:text-sm text-gray-600 break-all">Submission #{submission.submission_uuid}</p>
                <span className="hidden sm:inline text-gray-300">|</span>
                {isEditing ? (
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                    <label className="text-xs sm:text-sm text-gray-600 whitespace-nowrap">Zendesk Ticket ID:</label>
                    <input
                      type="number"
                      value={editedData.zendesk_ticket_id || ''}
                      onChange={(e) => handleFieldChange('zendesk_ticket_id', e.target.value ? parseInt(e.target.value) : null)}
                      placeholder="Enter ticket ID"
                      className="input text-sm w-full sm:w-32 py-1"
                    />
                  </div>
                ) : (
                  submission.zendesk_ticket_id ? (
                    <a
                      href={`https://tithely.zendesk.com/agent/tickets/${submission.zendesk_ticket_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs sm:text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
                    >
                      Zendesk Ticket #{submission.zendesk_ticket_id}
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  ) : (
                    <span className="text-xs sm:text-sm text-gray-500 italic">No ticket ID</span>
                  )
                )}
                {submission.zendesk_org_id && (
                  <>
                    <span className="hidden sm:inline text-gray-300">|</span>
                    <a
                      href={`https://team.tithely.com/organizations/${submission.zendesk_org_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs sm:text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
                    >
                      Team Portal
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </>
                )}
              </div>
            </div>
            <div className="flex gap-2 w-full lg:w-auto">
              {isEditing ? (
                <>
                  <button onClick={handleSaveChanges} className="btn btn-success flex-1 lg:flex-none text-sm">
                    Save Changes
                  </button>
                  <button onClick={handleEditToggle} className="btn btn-secondary flex-1 lg:flex-none text-sm">
                    Cancel
                  </button>
                </>
              ) : (
                canEdit(user?.role) && (
                  <button onClick={handleEditToggle} className="btn btn-primary w-full lg:w-auto text-sm">
                    Edit Submission
                  </button>
                )
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Status and Actions */}
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Status & Actions</h2>

              <div className="mb-4 space-y-2">
                <div>
                  <span className="text-sm text-gray-600 mr-2">Current Status:</span>
                  <span
                    className={`badge ${
                      submission.status === 'approved'
                        ? 'badge-approved'
                        : submission.status === 'rejected'
                        ? 'badge-rejected'
                        : 'badge-in-progress'
                    }`}
                  >
                    {formatStatusLabel(submission.status)}
                  </span>
                </div>

                {/* Twilio Verification Status */}
                {submission.twilio_campaign_sid && (
                  <div>
                    <span className="text-sm text-gray-600 mr-2">Twilio Status:</span>
                    <span className={`badge ${
                      submission.twilio_status === 'TWILIO_APPROVED' ? 'badge-approved' :
                      submission.twilio_status === 'TWILIO_REJECTED' ? 'badge-rejected' :
                      submission.twilio_status === 'IN_REVIEW' ? 'badge-warning' :
                      'badge-in-progress'
                    }`}>
                      {getTwilioStatusLabel(submission.twilio_status)}
                    </span>
                  </div>
                )}

                {/* Rejection Reason - shown prominently at top */}
                <TwilioRejectionReason
                  submission={submission}
                  title="Rejection Reason:"
                  className="mt-2"
                />

                {/* Verification SID */}
                {submission.twilio_campaign_sid && (
                  <div>
                    <span className="text-sm text-gray-600 mr-2">Verification SID:</span>
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                      {submission.twilio_campaign_sid}
                    </code>
                  </div>
                )}
              </div>

              {/* Suppress Emails Toggle */}
              {canEdit(user?.role) && (
                <div className="flex items-center gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="suppress-emails"
                    checked={isEditing ? (editedData.suppress_emails || false) : (submission.suppress_emails || false)}
                    onChange={(e) => {
                      if (isEditing) {
                        handleFieldChange('suppress_emails', e.target.checked);
                      } else {
                        // Allow toggling without entering edit mode
                        const newValue = e.target.checked;
                        submissionsAPI.update(id, { suppress_emails: newValue })
                          .then(() => {
                            toast.success(newValue ? 'Email notifications suppressed' : 'Email notifications enabled');
                            loadSubmission();
                          })
                          .catch((error) => {
                            toast.error(`Failed to update: ${error.response?.data?.error || error.message}`);
                          });
                      }
                    }}
                    className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="suppress-emails" className="text-sm text-gray-700">
                    <span className="font-medium">Suppress email notifications</span>
                    <span className="block text-xs text-gray-500">When enabled, status change emails will not be sent to the customer for this submission</span>
                  </label>
                  {submission.suppress_emails && !isEditing && (
                    <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      Emails Suppressed
                    </span>
                  )}
                </div>
              )}

              <div className="space-y-4">
                {/* Status Dropdown */}
                {canChangeStatus(user?.role) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Change Status
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={submission.status}
                        onChange={async (e) => {
                          const newStatus = e.target.value;
                          if (newStatus !== submission.status) {
                            const confirmed = await confirm({
                              title: 'Change Status',
                              message: `Change status to "${newStatus.replace(/_/g, ' ')}"?`,
                              confirmText: 'Change',
                              cancelText: 'Cancel',
                              variant: 'warning'
                            });
                            if (confirmed) {
                              handleStatusChange(newStatus);
                            } else {
                              // Reset the select value if cancelled
                              e.target.value = submission.status;
                            }
                          }
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="pending_review">Pending Review</option>
                        <option value="in_review">In Review</option>
                        <option value="ready_to_submit">Ready to Submit</option>
                        <option value="submitted_to_twilio">Submitted to Twilio</option>
                        <option value="awaiting_twilio">Awaiting Twilio</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="resubmitted">Resubmitted</option>
                        <option value="archived">Archived</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2">
                  {canSubmitToTwilio(user?.role) && (submission.status === 'ready_to_submit' || submission.status === 'rejected') && (
                    <button onClick={handleSubmitToTwilio} className="btn btn-primary">
                      Submit to Twilio
                    </button>
                  )}

                  {/*
                    Escape hatch for a rejected verification past its 7-day edit
                    window. Shown for any rejected submission that has a Twilio
                    verification; if the window is in fact still open the backend
                    refuses and tells the user to use Submit to Twilio instead,
                    so we never have to trust a possibly-stale local copy of
                    editExpiration to decide.
                  */}
                  {canRecreateVerification(user?.role) && submission.status === 'rejected' && submission.twilio_campaign_sid && (
                    <button
                      onClick={handleRecreateVerification}
                      disabled={recreating}
                      className="btn btn-danger"
                      title="Delete the rejected Twilio verification and submit a new one. Use this when the 7-day edit window has closed."
                    >
                      {recreating ? 'Recreating...' : 'Recreate Verification'}
                    </button>
                  )}

                  {canSubmitToTwilio(user?.role) && submission.status === 'awaiting_twilio' && (
                    <>
                      <button onClick={handleApprove} className="btn btn-success">
                        Mark as Approved
                      </button>
                      <button
                        onClick={() => setShowRejectModal(true)}
                        className="btn btn-danger"
                      >
                        Mark as Rejected
                      </button>
                    </>
                  )}

                  {/* Check Twilio Status Button */}
                  {canSubmitToTwilio(user?.role) && (submission.status === 'submitted_to_twilio' || submission.status === 'awaiting_twilio' || submission.status === 'rejected') && submission.twilio_campaign_sid && (
                    <button
                      onClick={handleCheckTwilioStatus}
                      disabled={checkingStatus}
                      className="btn btn-secondary"
                    >
                      {checkingStatus ? 'Checking...' : 'Check Twilio Status'}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <dt className="label">Organization Name</dt>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedData.organization_name || ''}
                      onChange={(e) => handleFieldChange('organization_name', e.target.value)}
                      className="input"
                    />
                  ) : (
                    <dd className="text-sm text-gray-900">{submission.organization_name}</dd>
                  )}
                </div>
                <div>
                  <dt className="label">Organization ID (Hub OID)</dt>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedData.zendesk_org_id || ''}
                      onChange={(e) => handleFieldChange('zendesk_org_id', e.target.value)}
                      className="input"
                      placeholder="Enter Hub OID"
                    />
                  ) : (
                    <dd className="text-sm text-gray-900">{submission.zendesk_org_id || 'N/A'}</dd>
                  )}
                </div>
                <div>
                  <dt className="label">Contact Name</dt>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedData.contact_name || ''}
                      onChange={(e) => handleFieldChange('contact_name', e.target.value)}
                      className="input"
                    />
                  ) : (
                    <dd className="text-sm text-gray-900">{submission.contact_name}</dd>
                  )}
                </div>
                <div>
                  <dt className="label">Contact Email</dt>
                  {isEditing ? (
                    <input
                      type="email"
                      value={editedData.contact_email || ''}
                      onChange={(e) => handleFieldChange('contact_email', e.target.value)}
                      className="input"
                    />
                  ) : (
                    <dd className="text-sm text-gray-900">{submission.contact_email}</dd>
                  )}
                </div>
                <div>
                  <dt className="label">Contact Phone</dt>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={editedData.contact_phone || ''}
                      onChange={(e) => handleFieldChange('contact_phone', e.target.value)}
                      className="input"
                    />
                  ) : (
                    <dd className="text-sm text-gray-900">{formatPhoneNumber(submission.contact_phone)}</dd>
                  )}
                </div>
                <div>
                  <dt className="label">Phone Number to Verify</dt>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={editedData.phone_number || ''}
                      onChange={(e) => handleFieldChange('phone_number', e.target.value)}
                      className="input"
                    />
                  ) : (
                    <dd className="text-sm text-gray-900 font-medium">{formatPhoneNumber(submission.phone_number)}</dd>
                  )}
                </div>
                <div>
                  <dt className="label">Phone Number Type</dt>
                  {isEditing ? (
                    <select
                      value={editedData.phone_number_type || 'local'}
                      onChange={(e) => handleFieldChange('phone_number_type', e.target.value)}
                      className="input"
                    >
                      <option value="local">Local</option>
                      <option value="toll-free">Toll-Free</option>
                    </select>
                  ) : (
                    <dd className="text-sm text-gray-900 capitalize">{submission.phone_number_type}</dd>
                  )}
                </div>
              </dl>
            </div>

            {/* Business Details */}
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Business Details</h2>
              <dl className="space-y-3">
                <div>
                  <dt className="label">Business Type</dt>
                  {isEditing ? (
                    <select
                      value={editedData.business_details?.business_type || ''}
                      onChange={(e) => handleBusinessDetailChange('business_type', e.target.value)}
                      className="input"
                    >
                      <option value="">Select business type...</option>
                      {BUSINESS_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <dd className="text-sm text-gray-900">
                      {submission.business_details?.business_type
                        ? getBusinessTypeLabel(submission.business_details.business_type)
                        : 'N/A'}
                    </dd>
                  )}
                </div>
                <div>
                  <dt className="label">Website</dt>
                  {isEditing ? (
                    <input
                      type="url"
                      value={editedData.business_details?.business_website || ''}
                      onChange={(e) => handleBusinessDetailChange('business_website', e.target.value)}
                      className="input"
                    />
                  ) : (
                    <dd className="text-sm text-gray-900">
                      {submission.business_details?.business_website ? (
                        <a
                          href={submission.business_details.business_website.startsWith('http')
                            ? submission.business_details.business_website
                            : `https://${submission.business_details.business_website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-700"
                        >
                          {submission.business_details.business_website}
                        </a>
                      ) : (
                        'N/A'
                      )}
                    </dd>
                  )}
                </div>
                <div>
                  <dt className="label">Doing Business As (DBA)</dt>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedData.business_details?.doing_business_as || ''}
                      onChange={(e) => handleBusinessDetailChange('doing_business_as', e.target.value)}
                      placeholder="DBA name (if different from legal name)"
                      className="input"
                    />
                  ) : (
                    <dd className="text-sm text-gray-900">{submission.business_details?.doing_business_as || 'N/A'}</dd>
                  )}
                </div>

                {/* Business Registration Number */}
                <div className={`p-3 rounded-lg ${!submission.business_details?.business_registration_number ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50'}`}>
                  <dt className="label mb-2">
                    Business Registration
                    {!isEditing && !submission.business_details?.business_registration_number && (
                      <span className="ml-2 text-xs font-medium text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded">Required - Missing</span>
                    )}
                  </dt>
                  {isEditing ? (
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={editedData.business_details?.business_registration_number || ''}
                        onChange={(e) => handleBusinessDetailChange('business_registration_number', e.target.value)}
                        placeholder="Registration Number"
                        className="input"
                      />
                      <select
                        value={editedData.business_details?.business_registration_authority || ''}
                        onChange={(e) => handleBusinessDetailChange('business_registration_authority', e.target.value)}
                        className="input"
                      >
                        <option value="">Select authority...</option>
                        {BUSINESS_REGISTRATION_AUTHORITIES.map((authority) => (
                          <option key={authority.value} value={authority.value}>
                            {authority.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <dd className="text-sm text-gray-900">
                      {submission.business_details?.business_registration_number ? (
                        <>
                          <span className="font-medium">{submission.business_details.business_registration_number}</span>
                          {submission.business_details.business_registration_authority && (
                            <span className="text-gray-500 ml-2">
                              ({getBusinessRegistrationAuthorityLabel(submission.business_details.business_registration_authority)})
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-gray-400 italic">Not provided</span>
                      )}
                    </dd>
                  )}
                </div>

                {/* Company Address */}
                <div>
                  <dt className="label">Company Address</dt>
                  {isEditing ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={editedData.business_details?.address?.street || ''}
                        onChange={(e) => handleAddressChange('street', e.target.value)}
                        placeholder="Street Address"
                        className="input"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          value={editedData.business_details?.address?.city || ''}
                          onChange={(e) => handleAddressChange('city', e.target.value)}
                          placeholder="City"
                          className="input"
                        />
                        <input
                          type="text"
                          value={editedData.business_details?.address?.state || ''}
                          onChange={(e) => handleAddressChange('state', e.target.value)}
                          placeholder="State"
                          className="input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          value={editedData.business_details?.address?.zip || ''}
                          onChange={(e) => handleAddressChange('zip', e.target.value)}
                          placeholder="ZIP Code"
                          className="input"
                        />
                        <input
                          type="text"
                          value={editedData.business_details?.address?.country || 'US'}
                          onChange={(e) => handleAddressChange('country', e.target.value)}
                          placeholder="Country"
                          className="input"
                        />
                      </div>
                    </div>
                  ) : (
                    <dd className="text-sm text-gray-900">
                      {submission.business_details?.address?.street ? (
                        <>
                          <div>{submission.business_details.address.street}</div>
                          <div>
                            {[
                              submission.business_details.address.city,
                              [submission.business_details.address.state, submission.business_details.address.zip]
                                .filter(Boolean)
                                .join(' ')
                            ]
                              .filter(Boolean)
                              .join(', ')}
                          </div>
                          {submission.business_details.address.country && (
                            <div>{submission.business_details.address.country}</div>
                          )}
                        </>
                      ) : (
                        'N/A'
                      )}
                    </dd>
                  )}
                </div>
                {/* Note: Use case, messaging volume, and opt-in process fields removed */}
                {/* These now use static values defined in backend/services/twilioService.js */}
              </dl>
            </div>

            {/* Customer Profile Status - shown when awaiting profile approval */}
            {submission.status === 'awaiting_profile_approval' && submission.customer_profile_sid && (
              <div className="card border-l-4 border-yellow-400">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Customer Profile Status</h2>
                  {canSubmitToTwilio(user?.role) && (
                    <button
                      onClick={handleCheckProfileStatus}
                      disabled={checkingStatus}
                      className="btn btn-primary text-sm"
                    >
                      {checkingStatus ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Checking...
                        </>
                      ) : (
                        'Check Profile Status'
                      )}
                    </button>
                  )}
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">Awaiting Twilio Approval</h3>
                      <p className="mt-1 text-sm text-yellow-700">
                        The Customer Profile is being reviewed by Twilio. This typically takes 1-2 hours.
                        Once approved, the toll-free verification will be automatically submitted.
                      </p>
                    </div>
                  </div>
                </div>
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Customer Profile SID</dt>
                    <dd className="text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded">
                      {submission.customer_profile_sid}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Status</dt>
                    <dd className="mt-1">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        PENDING APPROVAL
                      </span>
                    </dd>
                  </div>
                </dl>
              </div>
            )}

            {/* Twilio Details */}
            {submission.twilio_brand_sid && (
              <div className="card">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Toll-Free Verification</h2>
                  {canSubmitToTwilio(user?.role) && submission.twilio_campaign_sid && (submission.twilio_status === 'awaiting_twilio' || submission.twilio_status === 'TWILIO_REJECTED' || submission.status === 'rejected') && (
                    <button
                      onClick={handleCheckTwilioStatus}
                      disabled={checkingStatus}
                      className="btn btn-secondary text-sm"
                    >
                      {checkingStatus ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Checking...
                        </>
                      ) : (
                        '🔄 Check Status'
                      )}
                    </button>
                  )}
                </div>
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Business Profile SID</dt>
                    <dd className="text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded">
                      {submission.twilio_brand_sid}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Verification SID</dt>
                    <dd className="text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded">
                      {submission.twilio_campaign_sid}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Verification Status</dt>
                    <dd className="mt-1">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          submission.twilio_status === 'TWILIO_APPROVED'
                            ? 'bg-green-100 text-green-800'
                            : submission.twilio_status === 'PENDING_REVIEW' || submission.twilio_status === 'IN_REVIEW'
                            ? 'bg-yellow-100 text-yellow-800'
                            : submission.twilio_status === 'TWILIO_REJECTED'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {getTwilioStatusLabel(submission.twilio_status)}
                      </span>
                    </dd>
                  </div>
                  {submission.twilio_rejection_reason && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500 mb-2">Rejection Reason</dt>
                      <dd>
                        <TwilioRejectionReason submission={submission} />
                      </dd>
                    </div>
                  )}
                  {submission.submitted_to_twilio_at && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Submitted At</dt>
                      <dd className="text-sm text-gray-900">
                        {format(new Date(submission.submitted_to_twilio_at), 'MMM d, yyyy h:mm a')}
                      </dd>
                      {submission.submitted_to_twilio_by && (
                        <dd className="text-xs text-gray-500 mt-1">By: {submission.submitted_to_twilio_by}</dd>
                      )}
                    </div>
                  )}
                  {submission.twilio_response && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500 mb-2">Verification Details</dt>
                      <dd className="text-xs bg-gray-50 rounded p-3 overflow-x-auto">
                        <pre className="text-gray-700 whitespace-pre-wrap break-words">
                          {formatTwilioResponse(submission.twilio_response)}
                        </pre>
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Timeline */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-gray-500">Submitted</p>
                  <p className="text-gray-900">{format(new Date(submission.submitted_at), 'MMM d, yyyy h:mm a')}</p>
                </div>
                {submission.reviewed_at && (
                  <div>
                    <p className="text-gray-500">Reviewed</p>
                    <p className="text-gray-900">{format(new Date(submission.reviewed_at), 'MMM d, yyyy h:mm a')}</p>
                  </div>
                )}
                {submission.submitted_to_twilio_at && (
                  <div>
                    <p className="text-gray-500">Submitted to Twilio</p>
                    <p className="text-gray-900">
                      {format(new Date(submission.submitted_to_twilio_at), 'MMM d, yyyy h:mm a')}
                    </p>
                  </div>
                )}
                {submission.completed_at && (
                  <div>
                    <p className="text-gray-500">Completed</p>
                    <p className="text-gray-900">{format(new Date(submission.completed_at), 'MMM d, yyyy h:mm a')}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Internal Notes</h3>

              {canAddNotes(user?.role) && (
                <form onSubmit={handleAddNote} className="mb-4">
                  <textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Add a note..."
                    className="input mb-2"
                    rows={3}
                  />
                  <button type="submit" className="btn btn-primary w-full">
                    Add Note
                  </button>
                </form>
              )}

              <div className="space-y-3 max-h-96 overflow-y-auto">
                {submission.notes && submission.notes.length > 0 ? (
                  submission.notes.map((note) => (
                    <div key={note.id} className="bg-gray-50 rounded-lg p-3">
                      <p className="text-sm text-gray-900 mb-1">{note.note_text}</p>
                      <p className="text-xs text-gray-500">
                        {note.user_name} - {format(new Date(note.created_at), 'MMM d, h:mm a')}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">No notes yet</p>
                )}
              </div>
            </div>

            {/* Documents */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Documents</h3>

              {canUploadDocuments(user?.role) && (
                <div className="mb-4">
                  <label className="btn btn-primary w-full cursor-pointer">
                    {uploading ? 'Uploading...' : 'Upload Files'}
                    <input
                      type="file"
                      multiple
                      onChange={handleFileUpload}
                      disabled={uploading}
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                    />
                  </label>
                  <p className="text-xs text-gray-500 mt-2">
                    Max 10MB per file. Allowed: PDF, Images, Word, Excel
                  </p>
                </div>
              )}

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {documents.length > 0 ? (
                  documents.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{doc.file_name}</p>
                        <p className="text-xs text-gray-500">
                          {doc.uploaded_by_name || doc.uploaded_by} - {format(new Date(doc.created_at), 'MMM d, h:mm a')}
                        </p>
                        {doc.file_size && (
                          <p className="text-xs text-gray-400">
                            {(doc.file_size / 1024).toFixed(1)} KB
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {doc.download_url && (
                          <a
                            href={doc.download_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                          >
                            Download
                          </a>
                        )}
                        {canDeleteDocuments(user?.role) && (
                          <button
                            onClick={() => handleDeleteDocument(doc.id, doc.file_name)}
                            className="text-red-600 hover:text-red-700 text-sm font-medium"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">No documents uploaded</p>
                )}
              </div>
            </div>

            {/* Audit Log */}
            <div className="card">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Change History</h3>
                <button
                  onClick={() => setShowAuditLog(!showAuditLog)}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  {showAuditLog ? 'Hide' : 'Show'} ({auditLogs.length})
                </button>
              </div>

              {showAuditLog && (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {auditLogs.length > 0 ? (
                    auditLogs.map((log) => (
                      <div key={log.id} className="border-l-2 border-primary-200 pl-3 py-2">
                        <div className="flex items-start justify-between mb-1">
                          <p className="text-xs font-medium text-gray-900">
                            {log.action.replace(/_/g, ' ').toUpperCase()}
                          </p>
                          <p className="text-xs text-gray-500">
                            {format(new Date(log.created_at), 'MMM d, h:mm a')}
                          </p>
                        </div>
                        <p className="text-xs text-gray-600 mb-1">{log.user_name || log.user_email}</p>

                        {log.details?.changes && log.details.changes.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {log.details.changes.map((change, idx) => (
                              <div key={idx} className="text-xs bg-gray-50 rounded p-2">
                                <p className="font-medium text-gray-700">{change.field}</p>
                                <p className="text-gray-500">
                                  <span className="line-through">{change.old_value || '(empty)'}</span>
                                  {' → '}
                                  <span className="text-green-700 font-medium">{change.new_value || '(empty)'}</span>
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">No changes recorded</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Twilio submission error modal (field-specific guidance) */}
      {twilioErrorResponse && (
        <TwilioErrorModal
          errorResponse={twilioErrorResponse}
          onClose={() => setTwilioErrorResponse(null)}
          onViewSubmission={() => {
            setTwilioErrorResponse(null);
            setIsEditing(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          onRecreate={canRecreateVerification(user?.role) ? handleRecreateVerification : undefined}
          submissionTypeLabel={submission?.submission_type === 'messaging' ? 'Messaging' : 'Text Giving'}
          canEditConfigs={user?.role === 'admin'}
        />
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Reject Submission</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason..."
              className="input mb-4"
              rows={4}
            />
            <div className="flex gap-2">
              <button onClick={handleReject} className="btn btn-danger flex-1">
                Confirm Rejection
              </button>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                }}
                className="btn btn-secondary flex-1"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
